import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

// Formatea una fecha 'YYYY-MM-DD' como local (evita el desfase de un día por UTC)
const formatFecha = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length === 3) {
    return new Date(+parts[0], +parts[1] - 1, +parts[2]).toLocaleDateString('es-CO');
  }
  return new Date(dateStr).toLocaleDateString('es-CO');
};

const PER_PAGE = 10;

// Etiqueta y color según el estado de pago de la factura
const estadoPagoInfo = (paymentStatus) => {
  switch (paymentStatus) {
    case 'paid': return { label: 'Pagada', bg: '#d1fae5', color: '#065f46' };
    case 'partial': return { label: 'Pago parcial', bg: '#fef3c7', color: '#92400e' };
    default: return { label: 'Pendiente', bg: '#fee2e2', color: '#991b1b' };
  }
};

const ClienteHistorial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [historial, setHistorial] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadHistorial();
  }, [id, page]);

  const loadHistorial = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener historial de compras desde la API (paginado en servidor)
      const result = await clientesService.getHistorialCompras(id, {
        skip: (page - 1) * PER_PAGE,
        limit: PER_PAGE
      });

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.purchases || []);

        const historialData = dataArray.map(h => ({
          id: h.id,
          fecha: h.invoice_date || h.created_at,
          numeroFactura: h.invoice_number || `INV-${h.id}`,
          itemsCount: h.items_count || 0,
          total: h.total_amount || h.total || 0,
          pagado: h.paid_amount || 0,
          saldo: h.balance != null ? h.balance : (h.total_amount || 0) - (h.paid_amount || 0),
          paymentStatus: h.payment_status || 'unpaid',
          isOverdue: h.is_overdue,
          diasVencimiento: h.days_overdue
        }));

        setHistorial(historialData);
        if (typeof result.total === 'number') setTotal(result.total);
      } else {
        setError(result.message || 'Error al cargar el historial');
      }
    } catch (error) {
      console.error('Error cargando historial:', error);
      setError('Error de conexión al cargar el historial');
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const changePage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando historial..." />;
  }

  const totalPaginaActual = historial.reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="cliente-historial-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">HISTORIAL DE COMPRAS</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Resumen */}
      <div className="historial-summary">
        <div className="summary-card">
          <div className="summary-label">Total Facturas</div>
          <div className="summary-value">{total}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total (página actual)</div>
          <div className="summary-value">${totalPaginaActual.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Lista de historial */}
      {historial.length === 0 ? (
        <div className="empty-state">
          <p>No hay historial de compras para este cliente</p>
        </div>
      ) : (
      <>
      <div className="historial-list">
        {historial.map((venta) => {
          const estado = estadoPagoInfo(venta.paymentStatus);
          return (
          <div key={venta.id} className="historial-card">
            <div className="historial-header">
              <div className="factura-info">
                <strong>{venta.numeroFactura}</strong>
                <span className="fecha">{formatFecha(venta.fecha)}</span>
              </div>
              <div className="monto-info">
                <span className="total">${venta.total.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                <span
                  className="estado"
                  style={{
                    padding: '2px 10px',
                    borderRadius: '10px',
                    fontSize: '0.75em',
                    fontWeight: 600,
                    backgroundColor: estado.bg,
                    color: estado.color
                  }}
                >
                  {estado.label}
                </span>
              </div>
            </div>

            <div className="historial-body">
              <div className="producto-line">
                <span className="nombre">{venta.itemsCount} producto(s)</span>
                {venta.isOverdue && (
                  <span style={{ color: '#ef4444', fontSize: '0.85em' }}>
                    Vencida {venta.diasVencimiento ? `(${venta.diasVencimiento} días)` : ''}
                  </span>
                )}
              </div>
            </div>

            <div className="historial-footer">
              <div className="subtotals">
                <div className="subtotal-line">
                  <span>Pagado:</span>
                  <span>${venta.pagado.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="subtotal-line">
                  <span>Saldo:</span>
                  <span>${venta.saldo.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Paginador */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', margin: '20px 0', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => changePage(page - 1)}
            disabled={page <= 1}
            title="Página anterior"
            aria-label="Página anterior"
            style={{
              width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #7b2c7f',
              background: '#fff', color: '#7b2c7f', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1
            }}
          >
            <FiChevronLeft size={20} />
          </button>
          <span style={{ fontSize: '0.95rem', color: '#6b7280', fontWeight: 600 }}>
            Página {page} de {totalPages} · {total} facturas
          </span>
          <button
            type="button"
            onClick={() => changePage(page + 1)}
            disabled={page >= totalPages}
            title="Página siguiente"
            aria-label="Página siguiente"
            style={{
              width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #7b2c7f',
              background: '#fff', color: '#7b2c7f', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.4 : 1
            }}
          >
            <FiChevronRight size={20} />
          </button>
        </div>
      )}
      </>
      )}

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="historial" />
    </div>
  );
};

export default ClienteHistorial;
